import * as React from "react"

import { File as FileIcon, Upload, X } from "lucide-react"

import { cn } from "../../cn"
import { Button } from "../Button/Button"

function formatFileSize(bytes: number): string {
  if (bytes < 1024 * 1024) {
    return `${Math.max(1, Math.round(bytes / 1024))} KB`
  }
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export interface FileDropZoneProps {
  /** The chosen file, or null for the empty drop target. */
  file: File | null
  onFileChange: (file: File | null) => void
  /** Passed to the underlying file input, e.g. "audio/*,.mp3". */
  accept?: string
  /** Short hint under the prompt, e.g. "WAV or MP3". */
  hint?: React.ReactNode
  /** Accessible name for the drop target and its file input. */
  label?: string
  /** Icon shown beside a chosen file. Defaults to a generic file glyph. */
  fileIcon?: React.ReactNode
  /** Whether a chosen file shows a separate Change action. */
  showChangeButton?: boolean
  disabled?: boolean
  className?: string
}

/**
 * A drop target that is also a click target. Once a file is chosen it shows
 * the file with Change and Remove, so the same slot handles both states.
 */
const FileDropZone = React.forwardRef<HTMLDivElement, FileDropZoneProps>(
  (
    {
      file,
      onFileChange,
      accept,
      hint,
      label = "Choose a file",
      fileIcon,
      showChangeButton = true,
      disabled = false,
      className,
    },
    ref,
  ) => {
    const inputRef = React.useRef<HTMLInputElement>(null)
    const hintId = React.useId()
    const [isDragging, setIsDragging] = React.useState(false)

    const acceptFile = (candidate: File | undefined) => {
      if (candidate) {
        onFileChange(candidate)
      }
    }

    const input = (
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        className="sr-only"
        // The drop zone or the Change button is the one control; the input
        // only backs them, so it is neither a tab stop nor announced. The
        // label stays for tests and E2E that select the input by it.
        tabIndex={-1}
        aria-hidden="true"
        aria-label={label}
        disabled={disabled}
        onChange={(event) => acceptFile(event.target.files?.[0])}
      />
    )

    if (file) {
      return (
        <div
          ref={ref}
          className={cn(
            "flex items-center gap-3 rounded-md border border-coval-border bg-surface-0 px-3 py-2.5",
            className,
          )}
        >
          <span
            aria-hidden="true"
            className="shrink-0 text-coval-text-muted [&>svg]:size-4"
          >
            {fileIcon ?? <FileIcon />}
          </span>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium text-coval-text">
              {file.name}
            </p>
            <p className="text-xs text-coval-text-muted">
              {formatFileSize(file.size)}
            </p>
          </div>
          {showChangeButton && (
            <Button
              type="button"
              variant="secondary"
              className="h-7 px-2 text-xs"
              onClick={() => inputRef.current?.click()}
              disabled={disabled}
            >
              Change
            </Button>
          )}
          <Button
            type="button"
            icon
            variant="ghost"
            tooltip="Remove file"
            onClick={() => onFileChange(null)}
            disabled={disabled}
          >
            <X className="size-4" />
          </Button>
          {input}
        </div>
      )
    }

    return (
      <>
        <div
          ref={ref}
          role="button"
          tabIndex={disabled ? -1 : 0}
          aria-label={label}
          aria-describedby={hint ? hintId : undefined}
          aria-disabled={disabled}
          onClick={() => !disabled && inputRef.current?.click()}
          onKeyDown={(event) => {
            if (!disabled && (event.key === "Enter" || event.key === " ")) {
              event.preventDefault()
              inputRef.current?.click()
            }
          }}
          onDragOver={(event) => {
            event.preventDefault()
            if (!disabled) {
              setIsDragging(true)
            }
          }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={(event) => {
            event.preventDefault()
            setIsDragging(false)
            if (!disabled) {
              acceptFile(event.dataTransfer.files?.[0])
            }
          }}
          className={cn(
            "flex flex-col items-center justify-center gap-2 rounded-md border border-dashed px-4 py-8 text-center transition-colors",
            isDragging
              ? "border-coval-border-active bg-surface-default-hover"
              : "border-coval-border bg-surface-0 hover:border-coval-border-active",
            disabled ? "cursor-default opacity-60" : "cursor-pointer",
            className,
          )}
        >
          <Upload aria-hidden="true" className="size-5 text-coval-text-muted" />
          <p className="text-sm text-coval-text">
            Drop a file here, or{" "}
            <span className="font-medium underline underline-offset-2">
              browse
            </span>
          </p>
          {hint && (
            <p id={hintId} className="text-xs text-coval-text-muted">
              {hint}
            </p>
          )}
        </div>
        {input}
      </>
    )
  },
)

FileDropZone.displayName = "FileDropZone"

export { FileDropZone }
